/**
 * ErrorBoundary
 *
 * React's only way to catch render-time exceptions in component trees.
 * Sem isso, qualquer throw dentro de qualquer componente filho desmonta
 * a árvore inteira e o usuário vê "tela em branco" (white screen of death)
 * — único caminho é fechar e reabrir o app. Cliente novo reportou esse
 * exato sintoma em 2026-05-20 ao tentar editar paciente importado de outra
 * plataforma.
 *
 * Esta boundary é colocada em src/App.tsx envolvendo as rotas autenticadas.
 * Pra dev quiser uma boundary mais granular por feature, basta instanciar
 * de novo em qualquer lugar — boundaries aninhadas funcionam.
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Stack,
  Paper,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HomeIcon from '@mui/icons-material/Home';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
  children: React.ReactNode;
  /** Optional: callback when an error is captured (telemetry, sentry, etc) */
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    // Log estruturado: facilita debug futuro via console + telemetria.
    console.error('[ErrorBoundary] Captured render error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (cbErr) {
        // Engolir erro do callback pra não criar loop infinito de boundary.
        console.error('[ErrorBoundary] onError callback threw:', cbErr);
      }
    }
  }

  handleReset = () => {
    // Reset do state da boundary E reload da página atual. Reset sozinho
    // não basta se a causa do erro persiste em context/storage.
    this.setState({ error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = () => {
    // Volta pra dashboard (ou home pública se não logado).
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.error) {
      const isDev = process.env.NODE_ENV === 'development';
      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Paper elevation={2} sx={{ p: 4 }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main' }} />
              <Typography variant="h5" component="h1" fontWeight={600}>
                Algo deu errado
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Encontramos um erro inesperado ao carregar esta tela. Seus dados
                estão seguros — você pode tentar recarregar ou voltar ao início.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<RefreshIcon />}
                  onClick={this.handleReset}
                  size="large"
                >
                  Tentar Novamente
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeIcon />}
                  onClick={this.handleGoHome}
                  size="large"
                >
                  Voltar ao Início
                </Button>
              </Stack>

              {isDev && (
                <Alert severity="error" sx={{ mt: 2, textAlign: 'left', width: '100%' }}>
                  <AlertTitle>Detalhes técnicos (apenas em desenvolvimento)</AlertTitle>
                  <Box sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {this.state.error.message}
                    {this.state.error.stack && (
                      <Box sx={{ mt: 1, opacity: 0.7 }}>{this.state.error.stack}</Box>
                    )}
                    {this.state.errorInfo?.componentStack && (
                      <Box sx={{ mt: 1, opacity: 0.7 }}>
                        Component stack:
                        {this.state.errorInfo.componentStack}
                      </Box>
                    )}
                  </Box>
                </Alert>
              )}
            </Stack>
          </Paper>
        </Container>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
